import { useMemo, type Dispatch, type SetStateAction } from "react"
import { ProductTableRow } from "../connect"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import {
  PaginationState,
  RowSelectionState,
  OnChangeFn,
} from "@tanstack/react-table"
import { Loader } from "~/ui-rtk/components/ui/common"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

// Exported because the page reads the sort state TableVisualization writes to
// the URL under this key, then feeds it back into the cube query (the table
// runs manualSorting, so it never sorts rows itself).
export const CHART_ID = "product-page-funnel-table"

type TableRow = ProductTableRow

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 })

const formatPercent = (value: number) => `${value.toFixed(2)}%`

function ProductTitleCell({ row }: { row: TableRow }) {
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const hasValidTitle =
    row.productTitle && row.productTitle !== "Unnamed Product"
  return (
    <div className={cn("max-w-xs", isDemoEnabled && "blur-md")}>
      <div className="font-medium text-white truncate">
        {hasValidTitle ? row.productTitle : row.productHandle}
      </div>
      {hasValidTitle && (
        <div className="text-xs text-white/50 truncate">
          {row.productHandle}
        </div>
      )}
    </div>
  )
}

export const ALL_COLUMNS: TableVisualizationColumn[] = [
  {
    key: "productHandle",
    header: "Handle",
    type: "text",
    disabled: true,
  },
  {
    key: "productTitle",
    header: "Product",
    type: "text",
    width: 300,
    render: (row: TMetric) => (
      <ProductTitleCell row={row as unknown as TableRow} />
    ),
  },
  {
    key: "productViewers",
    header: "Product Views",
    type: "number",
    width: 140,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right font-medium">
          {formatNumber(typedRow.productViewers)}
        </div>
      )
    },
  },
  {
    key: "productToCartConversion",
    header: "Add to Cart Rate (Abandon)",
    type: "number",
    width: 200,
    disableSorting: true,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right">
          <div className="text-green-bright font-semibold">
            {formatPercent(typedRow.productToCartConversion)}
          </div>
          <div className="text-error text-xs">
            ({formatPercent(typedRow.productToCartAbandon)} abandon)
          </div>
        </div>
      )
    },
  },
  {
    key: "addersToCart",
    header: "Add to Cart",
    type: "number",
    width: 120,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right font-medium">
          {formatNumber(typedRow.addersToCart)}
        </div>
      )
    },
  },
  {
    key: "cartToCheckoutConversion",
    header: "Checkout Rate (Abandon)",
    type: "number",
    width: 200,
    disableSorting: true,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right">
          <div className="text-green-bright font-semibold">
            {formatPercent(typedRow.cartToCheckoutConversion)}
          </div>
          <div className="text-error text-xs">
            ({formatPercent(typedRow.cartToCheckoutAbandon)} abandon)
          </div>
        </div>
      )
    },
  },
  {
    key: "checkouts",
    header: "Checkouts",
    type: "number",
    width: 120,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right font-medium">
          {formatNumber(typedRow.checkouts)}
        </div>
      )
    },
  },
  {
    key: "checkoutToPurchaseConversion",
    header: "Purchase Rate (Abandon)",
    type: "number",
    width: 200,
    disableSorting: true,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right">
          <div className="text-green-bright font-semibold">
            {formatPercent(typedRow.checkoutToPurchaseConversion)}
          </div>
          <div className="text-error text-xs">
            ({formatPercent(typedRow.checkoutToPurchaseAbandon)} abandon)
          </div>
        </div>
      )
    },
  },
  {
    key: "purchasers",
    header: "Purchases",
    type: "number",
    width: 120,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right font-medium">
          {formatNumber(typedRow.purchasers)}
        </div>
      )
    },
  },
  {
    key: "productToPurchaseConversion",
    header: "Overall Conversion (Abandon)",
    type: "number",
    width: 220,
    disableSorting: true,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right">
          <div className="text-green-bright font-semibold">
            {formatPercent(typedRow.productToPurchaseConversion)}
          </div>
          <div className="text-error text-xs">
            ({formatPercent(typedRow.productToPurchaseAbandon)} abandon)
          </div>
        </div>
      )
    },
  },
  {
    key: "estimatedRevenue",
    header: "Estimated Revenue",
    type: "number",
    width: 180,
    disabled: true,
    disableSorting: true,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right font-medium">
          $
          {typedRow.estimatedRevenue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      )
    },
  },
  {
    key: "estimatedPerViewValue",
    header: "Estimated Per View Value",
    type: "number",
    width: 200,
    disabled: true,
    disableSorting: true,
    render: (row: TMetric) => {
      const typedRow = row as unknown as TableRow
      return (
        <div className="text-right font-medium">
          $
          {typedRow.estimatedPerViewValue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      )
    },
  },
]

interface ProductPageFunnelTableProps {
  data: ProductTableRow[]
  compareData?: ProductTableRow[]
  compareUniqueKey?: string
  paginationState: PaginationState
  setPaginationState: Dispatch<SetStateAction<PaginationState>>
  totalCount: number
  isLoading: boolean
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  columnConfig?: TableVisualizationColumn[]
  /** Export CSV button (rendered by TableVisualization when set). */
  csvExport?: () => Promise<void>
  isExporting?: boolean
  exportProgress?: { progress: number; message: string }
}

export default function ProductPageFunnelTable({
  data,
  compareData,
  compareUniqueKey = "productHandle",
  paginationState,
  setPaginationState,
  totalCount,
  isLoading,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
  columnConfig: columnConfigProp,
  csvExport,
  isExporting,
  exportProgress,
}: ProductPageFunnelTableProps) {
  // Convert data to metrics format
  const metrics = useMemo(() => {
    if (compareData && compareData.length > 0) {
      return [
        data.map(row => ({
          ...row,
          id: row.productId,
        })),
        compareData.map(row => ({
          ...row,
          id: row.productId,
        })),
      ]
    }
    return data.map(row => ({
      ...row,
      id: row.productId,
    }))
  }, [data, compareData])

  const columnConfig = useMemo<TableVisualizationColumn[]>(
    () => [
      ...(enableRowSelection
        ? [
            {
              key: "select",
              header: "",
              type: "text" as const,
              width: 50,
              disableSorting: true,
            },
          ]
        : []),
      ...(columnConfigProp ?? ALL_COLUMNS),
    ],
    [enableRowSelection, columnConfigProp],
  )

  if (!data.length && totalCount === 0 && !isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-system-gray-400">
        No data available for the selected filters.
      </div>
    )
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-basic-black/50 rounded-system-lg">
          <Loader size={50} />
        </div>
      )}
      <TableVisualization
        metrics={metrics as TMetric[]}
        columnConfig={columnConfig}
        paginationState={paginationState}
        setPaginationState={setPaginationState}
        enablePagination={true}
        chartId={CHART_ID}
        totalPages={Math.ceil(totalCount / paginationState.pageSize)}
        compareMode={Boolean(compareData && compareData.length > 0)}
        compareUniqueKey={compareUniqueKey}
        enableRowSelection={enableRowSelection}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        csvExport={csvExport}
        isExporting={isExporting}
        exportProgress={exportProgress}
      />
    </div>
  )
}
